// Campo y sección del editor con su «?»: etiqueta o título, ayuda, control y mensaje de error.
import { useId, type ReactNode } from 'react';
import { HelpHint } from '@/components/ui/HelpHint';
import { cn } from '@/lib/cn';
import { FIELD_MESSAGE_CLASS, SECTION, SECTION_TITLE_CLASS } from './EditorForm';

/** Etiqueta del campo sin margen: el margen lo pone la fila que también lleva el «?». */
const LABEL_TEXT = 'text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

/** El «?» de un campo o sección; su nombre accesible dice de qué es la ayuda. */
export function FieldHelp({ label, help }: { label: string; help: string }) {
    return <HelpHint text={help} label={`Ayuda: ${label}`} className="size-3.5" asSpan />;
}

export interface EditorFieldProps {
    label: string;
    help: string;
    error?: string;
    /** Nota bajo el control. */
    note?: ReactNode;
    /** Con función, la etiqueta apunta al control por `id`; sin ella, nombra al grupo. */
    children: ReactNode | ((id: string) => ReactNode);
    className?: string;
}

export function EditorField({ label, help, error, note, children, className }: EditorFieldProps) {
    const id = useId();
    const labelId = `${id}-label`;
    const errorId = `${id}-error`;
    const isRender = typeof children === 'function';
    return (
        <div
            className={cn('min-w-0', className)}
            {...(isRender
                ? {}
                : {
                      role: 'group',
                      'aria-labelledby': labelId,
                      ...(error ? { 'aria-invalid': true, 'aria-describedby': errorId } : {}),
                  })}
        >
            <div className="mb-1 flex items-center gap-1">
                {isRender ? (
                    <label id={labelId} htmlFor={id} className={LABEL_TEXT}>
                        {label}
                    </label>
                ) : (
                    <span id={labelId} className={LABEL_TEXT}>
                        {label}
                    </span>
                )}
                <FieldHelp label={label} help={help} />
            </div>
            {isRender ? children(id) : children}
            {note ? <div className="mt-1 text-[10.5px] text-muted-foreground">{note}</div> : null}
            {error ? (
                <p id={errorId} className={FIELD_MESSAGE_CLASS}>
                    {error}
                </p>
            ) : null}
        </div>
    );
}

export interface EditorSectionProps {
    title: string;
    help: string;
    /** Va a la derecha del título: lo elegido en una línea. */
    summary?: ReactNode;
    /** Sin línea de separación: para las columnas de la fila superior. */
    bare?: boolean;
    children: ReactNode;
}

export function EditorSection({
    title,
    help,
    summary,
    bare = false,
    children,
}: EditorSectionProps) {
    const Tag = bare ? 'div' : 'section';
    return (
        <Tag className={cn('min-w-0', !bare && SECTION)} {...(bare ? {} : { 'aria-label': title })}>
            <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                    <h3 className={cn(SECTION_TITLE_CLASS, 'mb-0')}>{title}</h3>
                    <FieldHelp label={title} help={help} />
                </div>
                {summary ? (
                    <div className="text-[10.5px] tabular-nums text-muted-foreground">
                        {summary}
                    </div>
                ) : null}
            </div>
            {children}
        </Tag>
    );
}

/** Monto con su unidad al lado: el mismo control en todos los editores. */
export function AmountInput({
    unit,
    prefix,
    className,
    ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { unit?: string; prefix?: string }) {
    return (
        <span className="flex items-center gap-1">
            {prefix ? <span className="text-xs text-muted-foreground">{prefix}</span> : null}
            <input
                inputMode="decimal"
                className={cn(
                    'w-[96px] rounded border border-border bg-background px-[7px] py-1 text-[11px] text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/15 disabled:opacity-60 aria-[invalid=true]:border-destructive',
                    className,
                )}
                {...props}
            />
            {unit ? <span className="text-[10.5px] text-muted-foreground">{unit}</span> : null}
        </span>
    );
}
